import dayjs, { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildTestCounts,
  type TestStatusKey,
} from '@/features/dashboard/components/dashboardStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/dashboard/hooks';
import { queryTestExecutions, queryTestExecutionStatusSummary } from '@/features/executions/data';
import { StackedMetricsStatusChartSection } from '@/features/shared/components/chartPanels/StackedMetricsStatusChartSection';
import { EntityTrendsPageShell } from '@/features/shared/components/EntityTrendsPageShell';
import {
  resolveMetaDateBounds,
  type TableQueryMeta,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { useCloneDateWindow } from '@/features/shared/hooks/useCloneDateWindow';
import { useEntityTrendsPage } from '@/features/shared/hooks/useEntityTrendsPage';
import { useSuiteOptions } from '@/features/suites';
import { useTestsQuery } from '@/features/tests';
import { TestExecutionsTable } from '@/features/tests/components/TestExecutionsTable';
import { TestsTable } from '@/features/tests/components/TestsTable';
import { queryTests, queryTestStatusSummary } from '@/features/tests/data/tests';
import {
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  type CloneExecutionSortDirection,
  cloneExecutionSortFieldMap,
  type CloneExecutionSortKey,
  cloneExecutionStatusOptions,
} from '@/features/tests/pages/testExecutionsTableDefinition';
import { TestsPageClonePanel } from '@/features/tests/pages/TestsPageClonePanel';
import {
  type CloneSortDirection,
  type CloneSortKey,
  testsTableDefinition,
} from '@/features/tests/pages/testsTableDefinition';
import { testExecutionSummaryStatusOptions } from '@/shared/constants/statusOptions';
import { gridPaths, queryKeys, useTagOptions } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { type GridColumnVisibilityModel } from '@/ui/primitives';
import type { MetricsStatusChartPanelModelToolbarState } from '@/features/shared/components/chartPanels/useMetricsStatusChartPanelModel';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  TestExecutionDto,
  TestExecutionStatus,
  TestExecutionStatusSummaryDto,
  TestListItemDto,
  TestSummaryDto,
} from '@flowqa/shared';

type CloneTestsPanelTab = 'tests' | 'executions';

export const TestsPage = (): ReactElement => {
  const viewModel = useEntityTrendsPage<
    TestListItemDto,
    TestSummaryDto | TestExecutionStatusSummaryDto
  >({
    loadingMessage: 'Loading tests...',
    metricsScope: 'test',
    gridPath: gridPaths.tests,
    executionGridPath: gridPaths.testExecutions,
    entitySearchField: 'name',
    executionSearchField: 'testName',
    statusOptions: testExecutionSummaryStatusOptions.map((option) => ({
      value: option.value as Exclude<TestStatusKey, 'untested'>,
      label: option.label,
    })),
    summaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testSummary(projectId, organisationId, filtersKey),
    executionSummaryKey: (projectId, organisationId, filtersKey) =>
      queryKeys.testExecutionSummary(projectId, organisationId, filtersKey),
    summaryPrefixKey: queryKeys.testSummaryPrefix,
    executionSummaryPrefixKey: queryKeys.testExecutionSummaryPrefix,
    queryLatestSummary: queryTestStatusSummary,
    queryExecutionSummary: queryTestExecutionStatusSummary,
    buildCounts: buildTestCounts,
    selectThresholds: (thresholds) => thresholds.tests,
    queryEntityTable: async (
      input: GridQueryInput,
      {
        selectedProjectId,
        selectedOrganisationId,
        rangeStartIso,
        rangeEndIso,
        previousRangeStartIso,
        previousRangeEndIso,
        deltasEnabled,
      },
    ): Promise<QueryResult<TestListItemDto>> =>
      queryTests(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        metricsStartDay: rangeStartIso ?? undefined,
        metricsEndDay: rangeEndIso ?? undefined,
        previousMetricsStartDay: previousRangeStartIso ?? undefined,
        previousMetricsEndDay: previousRangeEndIso ?? undefined,
        include: {
          suites: { ids: true, items: true, primary: true },
          latestExecution: true,
          executionStatusCounts: true,
          executionSummaries: true,
          metrics: true,
          metricDeltas: deltasEnabled && previousRangeStartIso !== null,
        },
      }),
  });
  const { tagOptions } = useTagOptions({ projectId: viewModel.selectedProjectId });
  const { suiteOptions } = useSuiteOptions({ projectId: viewModel.selectedProjectId });
  const testsOptionsQuery = useTestsQuery(viewModel.selectedProjectId, { enabled: true });
  const testOptions = useMemo(
    () =>
      (testsOptionsQuery.data?.items ?? []).map((test) => ({
        value: test.id,
        label: test.name,
      })),
    [testsOptionsQuery.data?.items],
  );
  const initialCloneColumnVisibilityModel: GridColumnVisibilityModel = {
    ...testsTableDefinition.defaultColumnVisibilityModel,
  };
  const initialCloneExecutionColumnVisibilityModel: GridColumnVisibilityModel = {
    ...cloneExecutionDefaultColumnVisibilityModel,
  };

  const [cloneDateQuickFilter, setCloneDateQuickFilter] = useState<CloneDateQuickFilter>('7d');
  const [cloneFromDate, setCloneFromDate] = useState<Dayjs | null>(null);
  const [cloneToDate, setCloneToDate] = useState<Dayjs | null>(null);
  const [debouncedCloneSearch, setDebouncedCloneSearch] = useState('');
  const [cloneColumnVisibilityModel, setCloneColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneColumnVisibilityModel);
  const [cloneSortKey, setCloneSortKey] = useState<CloneSortKey>(
    testsTableDefinition.defaultSortKey,
  );
  const [cloneSortDirection, setCloneSortDirection] = useState<CloneSortDirection>(
    testsTableDefinition.defaultSortDirection,
  );
  const [cloneFilterStatuses, setCloneFilterStatuses] = useState<TestExecutionStatus[]>([]);
  const [cloneFilterTests, setCloneFilterTests] = useState<string[]>([]);
  const [cloneFilterSuites, setCloneFilterSuites] = useState<string[]>([]);
  const [cloneFilterTags, setCloneFilterTags] = useState<string[]>([]);
  const [cloneActiveTab, setCloneActiveTab] = useState<CloneTestsPanelTab>('tests');
  const [debouncedCloneExecutionSearch, setDebouncedCloneExecutionSearch] = useState('');
  const [cloneExecutionColumnVisibilityModel, setCloneExecutionColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialCloneExecutionColumnVisibilityModel);
  const [cloneExecutionSortKey, setCloneExecutionSortKey] = useState<CloneExecutionSortKey>(
    cloneExecutionDefaultSortKey,
  );
  const [cloneExecutionSortDirection, setCloneExecutionSortDirection] =
    useState<CloneExecutionSortDirection>(cloneExecutionDefaultSortDirection);
  const [cloneExecutionFilterStatuses, setCloneExecutionFilterStatuses] = useState<
    TestExecutionStatus[]
  >([]);
  const [cloneExecutionFilterSuites, setCloneExecutionFilterSuites] = useState<string[]>([]);
  const [cloneExecutionFilterTags, setCloneExecutionFilterTags] = useState<string[]>([]);
  const [cloneTestsMeta, setCloneTestsMeta] = useState<TableQueryMeta<TestListItemDto> | null>(
    null,
  );
  const [cloneTestExecutionsMeta, setCloneTestExecutionsMeta] =
    useState<TableQueryMeta<TestExecutionDto> | null>(null);

  const cloneAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const cloneExecutionAvailableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const handleDebouncedCloneSearchChange = useCallback((value: string) => {
    setDebouncedCloneSearch(value);
  }, []);
  const handleDebouncedCloneExecutionSearchChange = useCallback((value: string) => {
    setDebouncedCloneExecutionSearch(value);
  }, []);
  const showCloneDeltas = viewModel.deltasEnabled;
  const cloneDateWindow = useCloneDateWindow({
    dateQuickFilter: cloneDateQuickFilter,
    fromDate: cloneFromDate,
    toDate: cloneToDate,
  });
  const cloneQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> => {
      const mappedFilters = input.filters.map((filter) =>
        filter.field === 'statusFilter'
          ? {
              ...filter,
              field: 'latestExecutionStatus',
            }
          : filter,
      );
      const toolbarFilters = mergeFilters(
        buildInFilter('latestExecutionStatus', cloneFilterStatuses),
        buildInFilter('id', cloneFilterTests),
        buildInFilter('suiteId', cloneFilterSuites),
        buildInFilter('tags', cloneFilterTags),
      );
      const sortOverride: ApiSort = {
        field: testsTableDefinition.sortFieldMap[cloneSortKey],
        direction: cloneSortDirection,
      };
      return queryTests(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          sorts: [sortOverride],
          filters: [...mappedFilters, ...toolbarFilters],
          metricsStartDay: cloneDateWindow.startDay ?? undefined,
          metricsEndDay: cloneDateWindow.endDay ?? undefined,
          previousMetricsStartDay: cloneDateWindow.previousStartDay ?? undefined,
          previousMetricsEndDay: cloneDateWindow.previousEndDay ?? undefined,
          include: {
            suites: { ids: true, items: true, primary: true },
            latestExecution: true,
            executionStatusCounts: true,
            executionSummaries: true,
            metrics: true,
            metricDeltas: showCloneDeltas,
          },
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.previousEndDay,
      cloneDateWindow.previousStartDay,
      cloneDateWindow.startDay,
      cloneFilterStatuses,
      cloneFilterTests,
      cloneFilterSuites,
      cloneFilterTags,
      cloneSortDirection,
      cloneSortKey,
      showCloneDeltas,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneExecutionQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestExecutionDto>> => {
      const isLatestQuickFilter =
        cloneDateQuickFilter === 'latest' && cloneFromDate === null && cloneToDate === null;
      const baseFilters = isLatestQuickFilter
        ? input.filters.filter(
            (filter) =>
              filter.field !== 'timestamp' &&
              filter.field !== 'createdAt' &&
              filter.field !== 'isLatest',
          )
        : input.filters;
      const toolbarFilters = mergeFilters(
        buildInFilter('status', cloneExecutionFilterStatuses),
        buildInFilter('suiteId', cloneExecutionFilterSuites),
        buildInFilter('tags', cloneExecutionFilterTags),
        isLatestQuickFilter
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              cloneDateWindow.startDay
                ? dayjs(cloneDateWindow.startDay).startOf('day').toISOString()
                : null,
              cloneDateWindow.endDay
                ? dayjs(cloneDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      );
      const sortOverride: ApiSort = {
        field: cloneExecutionSortFieldMap[cloneExecutionSortKey],
        direction: cloneExecutionSortDirection,
      };
      return queryTestExecutions(
        viewModel.selectedProjectId,
        viewModel.selectedOrganisationId ?? undefined,
        {
          ...input,
          search: debouncedCloneExecutionSearch,
          sorts: [sortOverride],
          filters: [...baseFilters, ...toolbarFilters],
        },
      );
    },
    [
      cloneDateWindow.endDay,
      cloneDateWindow.startDay,
      cloneDateQuickFilter,
      cloneFromDate,
      cloneToDate,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterSuites,
      cloneExecutionFilterTags,
      cloneExecutionSortDirection,
      cloneExecutionSortKey,
      debouncedCloneExecutionSearch,
      viewModel.selectedOrganisationId,
      viewModel.selectedProjectId,
    ],
  );
  const cloneNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No tests found for selected date range'
    : formatRangeWindowNoDataText('No tests found', cloneDateQuickFilter, false);
  const cloneExecutionsNoRowsText = cloneDateWindow.hasCustomBounds
    ? 'No test executions found for selected date range'
    : formatRangeWindowNoDataText('No test executions found', cloneDateQuickFilter, false);
  const cloneStatusFilterKey = useMemo(
    () => [...cloneFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterStatuses],
  );
  const testMetricCards = useMemo(
    () => viewModel.metricCards.filter((card) => card.id !== 'untested'),
    [viewModel.metricCards],
  );
  const cloneSuiteFilterKey = useMemo(
    () => [...cloneFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterSuites],
  );
  const cloneTestFilterKey = useMemo(
    () => [...cloneFilterTests].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTests],
  );
  const cloneTagFilterKey = useMemo(
    () => [...cloneFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneFilterTags],
  );
  const cloneExecutionStatusFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterStatuses],
  );
  const cloneExecutionSuiteFilterKey = useMemo(
    () =>
      [...cloneExecutionFilterSuites].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterSuites],
  );
  const cloneExecutionTagFilterKey = useMemo(
    () => [...cloneExecutionFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [cloneExecutionFilterTags],
  );
  useEffect(() => {
    if (cloneActiveTab === 'tests') {
      viewModel.handleSearchChange(debouncedCloneSearch);
      viewModel.handleStatusesChange(cloneFilterStatuses as TestStatusKey[]);
      viewModel.handleTagsChange(cloneFilterTags);
    } else {
      viewModel.handleSearchChange(debouncedCloneExecutionSearch);
      viewModel.handleStatusesChange(cloneExecutionFilterStatuses as TestStatusKey[]);
      viewModel.handleTagsChange(cloneExecutionFilterTags);
    }
    viewModel.handleRangeChange(cloneDateQuickFilter);
    viewModel.handleSelectedStartDayChange(
      cloneFromDate ? cloneFromDate.format('YYYY-MM-DD') : null,
    );
    viewModel.handleSelectedEndDayChange(cloneToDate ? cloneToDate.format('YYYY-MM-DD') : null);
  }, [
    cloneActiveTab,
    cloneDateQuickFilter,
    cloneExecutionFilterStatuses,
    cloneExecutionFilterTags,
    cloneFilterStatuses,
    cloneFilterTags,
    cloneFromDate,
    cloneToDate,
    debouncedCloneExecutionSearch,
    debouncedCloneSearch,
    viewModel,
  ]);
  const cloneTestExecutionDateBounds = useMemo(
    () => resolveMetaDateBounds(cloneTestExecutionsMeta, (item) => item.timestamp),
    [cloneTestExecutionsMeta],
  );
  const sharedChartToolbarState = useMemo<
    MetricsStatusChartPanelModelToolbarState<TestExecutionStatus>
  >(
    () =>
      cloneActiveTab === 'tests'
        ? {
            search: debouncedCloneSearch,
            setSearch: (value) => {
              handleDebouncedCloneSearchChange(value);
              viewModel.handleSearchChange(value);
            },
            selectedStatuses: cloneFilterStatuses,
            setSelectedStatuses: (next) => {
              setCloneFilterStatuses(next);
              viewModel.handleStatusesChange(next as TestStatusKey[]);
            },
            selectedTags: cloneFilterTags,
            setSelectedTags: (next) => {
              setCloneFilterTags(next);
              viewModel.handleTagsChange(next);
            },
            noRowsText: cloneNoRowsText,
            dateQuickFilter: cloneDateQuickFilter,
            onDateQuickFilterChange: (next) => {
              setCloneDateQuickFilter(next);
              setCloneFromDate(null);
              setCloneToDate(null);
              viewModel.handleRangeChange(next);
              viewModel.handleSelectedStartDayChange(null);
              viewModel.handleSelectedEndDayChange(null);
            },
            fromDate: cloneFromDate,
            toDate: cloneToDate,
            onFromDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneFromDate(nextValue);
              viewModel.handleSelectedStartDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
            onToDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneToDate(nextValue);
              viewModel.handleSelectedEndDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
          }
        : {
            search: debouncedCloneExecutionSearch,
            setSearch: (value) => {
              handleDebouncedCloneExecutionSearchChange(value);
              viewModel.handleSearchChange(value);
            },
            selectedStatuses: cloneExecutionFilterStatuses,
            setSelectedStatuses: (next) => {
              setCloneExecutionFilterStatuses(next);
              viewModel.handleStatusesChange(next as TestStatusKey[]);
            },
            selectedTags: cloneExecutionFilterTags,
            setSelectedTags: (next) => {
              setCloneExecutionFilterTags(next);
              viewModel.handleTagsChange(next);
            },
            noRowsText: cloneExecutionsNoRowsText,
            dateQuickFilter: cloneDateQuickFilter,
            onDateQuickFilterChange: (next) => {
              setCloneDateQuickFilter(next);
              setCloneFromDate(null);
              setCloneToDate(null);
              viewModel.handleRangeChange(next);
              viewModel.handleSelectedStartDayChange(null);
              viewModel.handleSelectedEndDayChange(null);
            },
            fromDate: cloneFromDate,
            toDate: cloneToDate,
            onFromDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneFromDate(nextValue);
              viewModel.handleSelectedStartDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
            onToDateChange: (value) => {
              const nextValue = value ? dayjs(value) : null;
              setCloneToDate(nextValue);
              viewModel.handleSelectedEndDayChange(
                nextValue ? nextValue.format('YYYY-MM-DD') : null,
              );
            },
          },
    [
      cloneActiveTab,
      cloneDateQuickFilter,
      cloneExecutionFilterStatuses,
      cloneExecutionFilterTags,
      cloneExecutionsNoRowsText,
      cloneFilterStatuses,
      cloneFilterTags,
      cloneFromDate,
      cloneNoRowsText,
      cloneToDate,
      debouncedCloneExecutionSearch,
      debouncedCloneSearch,
      handleDebouncedCloneExecutionSearchChange,
      handleDebouncedCloneSearchChange,
    ],
  );
  const cloneContentOverride = (
    <Stack gap="2xl">
      <StackedMetricsStatusChartSection<TestExecutionStatus>
        searchAriaLabel={
          cloneActiveTab === 'tests'
            ? 'Search stacked test chart data'
            : 'Search stacked test execution chart data'
        }
        noRowsBaseText={cloneActiveTab === 'tests' ? 'No tests found' : 'No test executions found'}
        scope="test"
        projectId={viewModel.selectedProjectId}
        availableTags={
          cloneActiveTab === 'tests' ? cloneAvailableTags : cloneExecutionAvailableTags
        }
        metricCards={testMetricCards}
        loading={viewModel.showMetricCardsSkeleton}
        statusCardSkeletonCount={5}
        selectedStatusIds={viewModel.selectedStatuses}
        onStatusToggle={(status) => {
          if (status === 'total') return;
          if (cloneActiveTab === 'tests') {
            setCloneFilterStatuses((prev) =>
              prev.includes(status as TestExecutionStatus)
                ? prev.filter((item) => item !== status)
                : [...prev, status as TestExecutionStatus],
            );
          } else {
            setCloneExecutionFilterStatuses((prev) =>
              prev.includes(status as TestExecutionStatus)
                ? prev.filter((item) => item !== status)
                : [...prev, status as TestExecutionStatus],
            );
          }
          viewModel.handleStatusToggle(status as TestStatusKey);
        }}
        toolbarState={sharedChartToolbarState}
        statusOptions={
          cloneActiveTab === 'tests'
            ? testsTableDefinition.statusOptions
            : cloneExecutionStatusOptions
        }
        statusFilterLabel="Status"
        suppressAnimation={viewModel.suppressChartAnimation}
        showPoints={viewModel.showLineChartPoints}
      />
      <TestsPageClonePanel
        cloneActiveTab={cloneActiveTab}
        setCloneActiveTab={setCloneActiveTab}
        cloneDateQuickFilter={cloneDateQuickFilter}
        setCloneDateQuickFilter={setCloneDateQuickFilter}
        cloneFromDate={cloneFromDate}
        setCloneFromDate={setCloneFromDate}
        cloneToDate={cloneToDate}
        setCloneToDate={setCloneToDate}
        cloneColumnVisibilityModel={cloneColumnVisibilityModel}
        setCloneColumnVisibilityModel={setCloneColumnVisibilityModel}
        cloneSortKey={cloneSortKey}
        setCloneSortKey={setCloneSortKey}
        cloneSortDirection={cloneSortDirection}
        setCloneSortDirection={setCloneSortDirection}
        cloneFilterStatuses={cloneFilterStatuses}
        setCloneFilterStatuses={setCloneFilterStatuses}
        cloneFilterTests={cloneFilterTests}
        setCloneFilterTests={setCloneFilterTests}
        cloneFilterSuites={cloneFilterSuites}
        setCloneFilterSuites={setCloneFilterSuites}
        cloneFilterTags={cloneFilterTags}
        setCloneFilterTags={setCloneFilterTags}
        cloneExecutionColumnVisibilityModel={cloneExecutionColumnVisibilityModel}
        setCloneExecutionColumnVisibilityModel={setCloneExecutionColumnVisibilityModel}
        cloneExecutionSortKey={cloneExecutionSortKey}
        setCloneExecutionSortKey={setCloneExecutionSortKey}
        cloneExecutionSortDirection={cloneExecutionSortDirection}
        setCloneExecutionSortDirection={setCloneExecutionSortDirection}
        cloneExecutionFilterStatuses={cloneExecutionFilterStatuses}
        setCloneExecutionFilterStatuses={setCloneExecutionFilterStatuses}
        cloneExecutionFilterSuites={cloneExecutionFilterSuites}
        setCloneExecutionFilterSuites={setCloneExecutionFilterSuites}
        cloneExecutionFilterTags={cloneExecutionFilterTags}
        setCloneExecutionFilterTags={setCloneExecutionFilterTags}
        cloneAvailableTags={cloneAvailableTags}
        cloneExecutionAvailableTags={cloneExecutionAvailableTags}
        suiteOptions={suiteOptions}
        testOptions={testOptions}
        cloneDateWindow={cloneDateWindow}
        cloneStatusFilterKey={cloneStatusFilterKey}
        cloneTestFilterKey={cloneTestFilterKey}
        cloneSuiteFilterKey={cloneSuiteFilterKey}
        cloneTagFilterKey={cloneTagFilterKey}
        cloneExecutionStatusFilterKey={cloneExecutionStatusFilterKey}
        cloneExecutionSuiteFilterKey={cloneExecutionSuiteFilterKey}
        cloneExecutionTagFilterKey={cloneExecutionTagFilterKey}
        cloneNoRowsText={cloneNoRowsText}
        cloneExecutionsNoRowsText={cloneExecutionsNoRowsText}
        debouncedCloneSearch={debouncedCloneSearch}
        debouncedCloneExecutionSearch={debouncedCloneExecutionSearch}
        cloneTestsMeta={cloneTestsMeta}
        setCloneTestsMeta={setCloneTestsMeta}
        cloneTestExecutionsMeta={cloneTestExecutionsMeta}
        setCloneTestExecutionsMeta={setCloneTestExecutionsMeta}
        cloneTestExecutionDateBounds={cloneTestExecutionDateBounds}
        showCloneDeltas={showCloneDeltas}
        selectedOrganisationId={viewModel.selectedOrganisationId}
        selectedProjectId={viewModel.selectedProjectId}
        handleSearchChange={viewModel.handleSearchChange}
        handleRangeChange={viewModel.handleRangeChange}
        handleSelectedStartDayChange={viewModel.handleSelectedStartDayChange}
        handleSelectedEndDayChange={viewModel.handleSelectedEndDayChange}
        handleStatusesChange={viewModel.handleStatusesChange}
        handleTagsChange={viewModel.handleTagsChange}
        handleDebouncedCloneSearchChange={handleDebouncedCloneSearchChange}
        handleDebouncedCloneExecutionSearchChange={handleDebouncedCloneExecutionSearchChange}
        cloneQueryFn={cloneQueryFn}
        cloneExecutionQueryFn={cloneExecutionQueryFn}
      />
    </Stack>
  );

  return (
    <Stack gap="md">
      <EntityTrendsPageShell
        viewModel={viewModel}
        title="Tests"
        addButtonLabel="Add Test"
        searchAriaLabel="Search tests"
        chartTabsAriaLabel="Tests chart tabs"
        pageTabsAriaLabel="Tests page tabs"
        entityTabLabel="Tests"
        chartEmptyStateTitle="No test executions found"
        entityTableNoRowsText="No tests found"
        executionTableNoRowsText="No test executions found"
        persistKey="tests-table"
        executionPersistKey="test-executions-table"
        contentOverride={cloneContentOverride}
        renderEntityTable={({
          queryKey,
          queryFn,
          fixedFilters,
          search,
          showDeltas,
          persistKey,
          noRowsText,
        }) => (
          <TestsTable
            persistKey={persistKey}
            queryKey={queryKey}
            queryFn={queryFn}
            pageSizeOptions={[5]}
            initialPageSize={5}
            disablePageSizeSelect
            showDeltas={showDeltas}
            fixedFilters={fixedFilters}
            search={search}
            noRowsText={noRowsText}
          />
        )}
        renderExecutionsTable={({ fixedFilters, search, persistKey, noRowsText }) => (
          <TestExecutionsTable
            persistKey={persistKey}
            fixedFilters={fixedFilters}
            pageSizeOptions={[5]}
            initialPageSize={5}
            disablePageSizeSelect
            search={search}
            noRowsText={noRowsText}
          />
        )}
      />
    </Stack>
  );
};
